import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Building2,
  UtensilsCrossed,
  CalendarDays,
  Palette,
  Camera,
  CheckCircle2,
  Sparkles,
  ArrowRight
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  {
    id: "Convention Hall",
    testId: TEST_IDS.categoryOptionConventionHall,
    title: "Convention Hall",
    subtitle: "Banquet Halls, Kalyana Mantapa & Venues",
    description: "Manage hall availability, single/multi-day functions, guest capacity, and slot conflicts.",
    icon: Building2,
    badge: "Most Popular",
    color: "from-pink-900 to-pink-700"
  },
  {
    id: "Catering Services",
    testId: TEST_IDS.categoryOptionCatering,
    title: "Catering Services",
    subtitle: "Buffets, Traditional Feasts & Dining",
    description: "Manage menu packages, plate counts, kitchen schedules, and event meal slots.",
    icon: UtensilsCrossed,
    color: "from-rose-800 to-rose-600"
  },
  {
    id: "Event Planner",
    testId: TEST_IDS.categoryOptionPlanner,
    title: "Event Planner",
    subtitle: "End-to-End Wedding & Event Coordination",
    description: "Manage client itineraries, vendor coordination, milestones, and master budgets.",
    icon: CalendarDays,
    color: "from-pink-800 to-fuchsia-700"
  },
  {
    id: "Decoration",
    testId: TEST_IDS.categoryOptionDecoration,
    title: "Decoration",
    subtitle: "Floral, Stage, Lighting & Mandap Setup",
    description: "Manage theme catalogs, prop inventory, setup timelines, and decor packages.",
    icon: Palette,
    color: "from-fuchsia-800 to-pink-600"
  },
  {
    id: "Photography & Videography",
    testId: TEST_IDS.categoryOptionPhotography,
    title: "Photography & Videography",
    subtitle: "Cinematography, Shoots & Live Streaming",
    description: "Manage crew assignments, shoot dates, deliverables, and drone coverage.",
    icon: Camera,
    color: "from-pink-950 to-rose-700"
  }
];

export default function CategoryOnboardingPage({ onCategorySaved }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("Convention Hall");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!selected) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/profile/category`, { category: selected });
      localStorage.setItem("bme_category_selected", "true");
      if (onCategorySaved) onCategorySaved(selected);
      navigate("/profile");
    } catch (err) {
      alert("Failed to save category.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl border border-pink-100 overflow-hidden flex flex-col animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-8 text-white text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-semibold mb-2">
            <Sparkles className="w-3.5 h-3.5 text-pink-200" />
            <span>One-Time Business Setup</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Select Your Business Category
          </h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1 max-w-md mx-auto">
            Choose what best describes your event business. This tailors your calendar, slots, and booking workflow.
          </p>
        </div>

        {/* Content list */}
        <div className="p-6 sm:p-8 space-y-3.5 bg-pink-50/20">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isSelected = selected === cat.id;

            return (
              <div
                key={cat.id}
                data-testid={cat.testId}
                onClick={() => setSelected(cat.id)}
                className={`group relative p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                  isSelected
                    ? "bg-pink-50 border-pink-700 shadow-md scale-[1.01]"
                    : "bg-white border-pink-100 hover:border-pink-300 hover:bg-pink-50/50"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cat.color} text-white flex items-center justify-center flex-shrink-0 shadow-sm`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900 group-hover:text-pink-950">
                        {cat.title}
                      </h3>
                      {cat.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-pink-100 text-pink-800 px-2 py-0.5 rounded-full">
                          {cat.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-pink-700 mt-0.5">
                      {cat.subtitle}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-center self-center pl-2">
                    {isSelected ? (
                      <CheckCircle2 className="w-6 h-6 text-pink-700 flex-shrink-0 animate-scale-in" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-pink-400" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="p-6 bg-white border-t border-pink-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500 text-center sm:text-left">
            Saved permanently to your BookMyEvents profile.
          </p>
          <button
            data-testid={TEST_IDS.categoryConfirmBtn}
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-gradient-to-r from-pink-900 via-pink-800 to-rose-700 text-white font-bold text-sm shadow-lg hover:shadow-pink-900/30 hover:opacity-95 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <span>{isSubmitting ? "Saving..." : "Continue to Business Setup"}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
